import {
  AsYouType,
  CountryCallingCode,
  CountryCode,
  parsePhoneNumber,
  parsePhoneNumberFromString,
  PhoneNumber
} from 'libphonenumber-js/max';
import { PhoneNumberModel } from '@utmost.co/enterprise-models';
import { DEFAULT_COUNTRY_CODE } from './phoneNumberConst';
import { FormatterOutput, PhoneNumberWithAreaCode } from './phoneNumberTypes';

export const phoneNumberUtil = {
  isValid(text?: string, countryCode: CountryCode = DEFAULT_COUNTRY_CODE): boolean {
    if (!text) {
      return true;
    }
    let isValid;
    try {
      isValid = parsePhoneNumber(text ?? '', countryCode).isValid();
    } catch (e) {
      isValid = false;
    }
    return isValid;
  },
  isPossible(text?: string, countryCode: CountryCode = DEFAULT_COUNTRY_CODE): boolean {
    if (!text) {
      return true;
    }
    let isPossible;
    try {
      isPossible = parsePhoneNumber(text ?? '', countryCode).isPossible();
    } catch (e) {
      isPossible = false;
    }
    return isPossible;
  },
  isEmpty: (text?: string) => !text,
  getRawNumber,
  isDigit,
  findFirstDigit,
  formatter(countryCode: CountryCode = DEFAULT_COUNTRY_CODE): FormatterOutput {
    const asYouType = new AsYouType(countryCode);
    return {
      reset: asYouType.reset,
      getNumber: asYouType.getNumber,
      getTemplate: asYouType.getTemplate,
      format: (text: string): string => {
        asYouType.reset();
        return asYouType.input(getRawNumber(text, true));
      },
      input: (e, prevText = '') => {
        let text = e?.target?.value ?? '';
        const isBackspace = (e.nativeEvent as any)?.inputType === 'deleteContentBackward';
        const caretPosition = e.target.selectionStart ?? 0;
        asYouType.reset();
        if (!!prevText && getRawNumber(text) === getRawNumber(prevText) && !isDigit(prevText.charAt(caretPosition))) {
          const searchText = isBackspace
            ? prevText.slice(0, caretPosition).split('').reverse().join('')
            : prevText.slice(caretPosition);
          const { index, exists } = findFirstDigit(searchText);
          if (exists) {
            const d = isBackspace ? searchText.length - (index + 1) : caretPosition + (index - 1);
            text = text.substring(0, d) + text.substring(d + 1);
          }
        }
        return asYouType.input(getRawNumber(text, true));
      }
    } as FormatterOutput;
  },
  phoneNumberModelToString(phone?: Partial<PhoneNumberModel>, countryCode: CountryCode = DEFAULT_COUNTRY_CODE): string {
    if (!phone || (!phone.number && !phone.countryCode)) {
      return '';
    }
    const isNumberFromOneValue = !!phone.number && !phone.countryCode;
    if (isNumberFromOneValue) {
      return phoneNumberUtil.parseFromString(phone.number ?? '', countryCode)?.internationalFormat ?? '';
    }
    try {
      const value = `${phone.countryCode ? `+${phone.countryCode}` : ''}${phone.areaCode ?? ''}${phone.number}`;
      const result = parsePhoneNumber(value).formatInternational();
      return result || value;
    } catch (e) {
      return '';
    }
  },
  stringToPhoneNumberModel(text: string, countryCode: CountryCode = DEFAULT_COUNTRY_CODE): Partial<PhoneNumberModel> {
    const details = phoneNumberUtil.parseFromString(text, countryCode);
    return {
      areaCode: details?.areaCode,
      number: (details?.number as string) ?? text,
      countryCode: details?.countryCallingCode as string,
      extension: details?.ext as string
    };
  },
  parseFromString(text: string, countryCode: CountryCode = DEFAULT_COUNTRY_CODE): PhoneNumberWithAreaCode | undefined {
    const textWithCode = withMissingUsCountryCode(text, countryCode);
    const details: PhoneNumber | undefined = parsePhoneNumberFromString(textWithCode, countryCode);
    let areaCode: string | undefined;
    let number: string = details?.nationalNumber ?? text;
    const internationalFormat = details?.formatInternational() ?? textWithCode;

    if (supportsAreaCode(details?.country, details?.countryCallingCode)) {
      areaCode = details?.nationalNumber.replace(/\D/g, '').substr(0, 3);
      number = number.substr(3);
    }

    delete (details as any)?.metadata;
    return {
      ...details,
      areaCode,
      number,
      isValid: phoneNumberUtil.isValid(withMissingUsCountryCode(text, countryCode), countryCode),
      isPossible: phoneNumberUtil.isPossible(withMissingUsCountryCode(text, countryCode), countryCode),
      internationalFormat
    } as PhoneNumberWithAreaCode;
  }
};

function findFirstDigit(text: string): { index: number; sliced: string; exists: boolean; digit: string } {
  const indexOfFirstDigit = text.search(/\d/);
  return {
    index: indexOfFirstDigit,
    digit: text.charAt(indexOfFirstDigit),
    exists: indexOfFirstDigit !== -1,
    sliced: text.slice(indexOfFirstDigit >= 0 ? indexOfFirstDigit + 1 : -1)
  };
}

function supportsAreaCode(country?: CountryCode, code?: CountryCallingCode): boolean {
  return country === 'US' || country === 'CA' || code === '1';
}

function withMissingUsCountryCode(text: string, countryCode: CountryCode = DEFAULT_COUNTRY_CODE): string {
  if (countryCode !== 'US') {
    return text;
  }
  let result = text;
  const hasPlus = text.charAt(0) === '+';
  const hasCountryCode = hasPlus ? text.charAt(1) === '1' : text.charAt(0) === '1';
  if (!hasPlus) {
    if (!hasCountryCode) {
      result = `+1${result}`;
    } else {
      result = `+${result}`;
    }
  }
  return result;
}

function getRawNumber(inputText: string, keepPlus: boolean = false): string {
  const prefix = keepPlus && inputText.charAt(0) === '+' ? '+' : '';
  return prefix + (inputText.match(/\d+/g) ?? []).join('');
}

function isDigit(char: string): boolean {
  return Number.isInteger(parseInt(char, 10));
}
